import { useState, useEffect, useCallback, useMemo } from "react";
import { FilterableList } from "../../FilterableList";
import type { FilterableSection } from "../../FilterableList";
import type { Flag } from "./types";
import { getCachedData, subscribe, fetchFlags, submitForm } from "./store";
import { FlagRow } from "./FlagRow";

function groupByTeam(flags: Flag[]): Record<string, Flag[]> {
  const groups: Record<string, Flag[]> = {};
  for (const flag of flags) {
    const team = flag.team || "Ungrouped";
    if (!groups[team]) groups[team] = [];
    groups[team].push(flag);
  }
  return groups;
}

export function FeatureFlagsPanel() {
  const [, forceUpdate] = useState(0);
  const [teamFilter, setTeamFilter] = useState<string>("all");
  const [showOverridesOnly, setShowOverridesOnly] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const data = getCachedData();

  useEffect(() => subscribe(() => forceUpdate((n) => n + 1)), []);

  const handleRefresh = useCallback(async () => {
    setIsLoading(true);
    await fetchFlags(true);
    setIsLoading(false);
  }, []);

  const handleToggle = useCallback((flag: Flag) => {
    submitForm("/dev_tools/feature-flags/toggle", {
      name: flag.name,
      active: String(!flag.active),
    });
  }, []);

  const handleClearOverrides = useCallback(() => {
    submitForm("/dev_tools/feature-flags/clear", {});
  }, []);

  const preFiltered = useMemo(() => {
    if (!data) return [];
    return data.flags.filter((f) => {
      if (showOverridesOnly && !f.overridden) return false;
      if (teamFilter !== "all" && (f.team || "Ungrouped") !== teamFilter) return false;
      return true;
    });
  }, [data, teamFilter, showOverridesOnly]);

  const overrideCount = data?.flags.filter((f) => f.overridden).length ?? 0;

  const renderFlags = useCallback(
    (flags: Flag[]) => (
      <>
        {flags.map((flag) => (
          <FlagRow key={flag.name} flag={flag} onToggle={handleToggle} />
        ))}
      </>
    ),
    [handleToggle],
  );

  const sections: FilterableSection<Flag>[] = useMemo(() => {
    const grouped = groupByTeam(preFiltered);
    return Object.entries(grouped).map(([team, flags]) => {
      const teamOverrides = flags.filter((f) => f.overridden).length;
      return {
        key: team,
        title: `${team}${teamOverrides > 0 ? ` (${teamOverrides} overridden)` : ""}`,
        items: flags,
        matchText: (f: Flag) => f.name,
        renderItems: renderFlags,
        defaultOpen: Object.keys(grouped).length <= 3,
        className: teamOverrides > 0 ? "dt-feature-flags__group--overridden" : undefined,
      };
    });
  }, [preFiltered, renderFlags]);

  if (!data) {
    return <div className="dt-status-banner__text">Loading...</div>;
  }

  return (
    <>
      <div className="dt-feature-flags__filters">
        <select
          className="dt-form-select"
          value={teamFilter}
          onChange={(e) => setTeamFilter(e.target.value)}
        >
          <option value="all">All teams</option>
          {data.teams.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
          <option value="Ungrouped">Ungrouped</option>
        </select>

        <label className="dt-feature-flags__checkbox-label">
          <input
            type="checkbox"
            checked={showOverridesOnly}
            onChange={(e) => setShowOverridesOnly(e.target.checked)}
          />
          Overrides only
        </label>

        <button
          className="dt-btn"
          onClick={handleRefresh}
          disabled={isLoading}
          type="button"
        >
          {isLoading ? "..." : "Refresh"}
        </button>
      </div>

      <div className="dt-section-label">
        {preFiltered.length} flag{preFiltered.length === 1 ? "" : "s"}
        {overrideCount > 0 && ` · ${overrideCount} overridden`}
      </div>

      <FilterableList sections={sections} placeholder="Search flags..." />

      {preFiltered.length === 0 && (
        <div className="dt-status-banner">
          <div className="dt-status-banner__text">No flags match your filters</div>
        </div>
      )}

      {overrideCount > 0 && (
        <>
          <div className="dt-section-label" style={{ marginTop: 16 }} />
          <button
            className="dt-btn"
            onClick={handleClearOverrides}
            type="button"
            style={{ width: "100%" }}
          >
            Clear All Overrides ({overrideCount})
          </button>
        </>
      )}
    </>
  );
}
